import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Trash2, Plus, Camera, X, Image as ImageIcon, FileText, CheckCircle2, Link2, ArrowLeft, Briefcase } from "lucide-react";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";
import MicButton from "../components/MicButton";
import { usePrefetch } from "../hooks/usePrefetch";
import MissingItemsHint from "../components/MissingItemsHint";
import { compressImage } from "../lib/imageCompression";

const blankItem = () => ({
  description: "", quantity: 1, unit: "u",
  material_cost: 0, labor_cost: 0, equipment_cost: 0, subcontractor_cost: 0, profit_pct: 15, total: 0,
});

const computeTotal = (it) => {
  const cost = (Number(it.material_cost) + Number(it.labor_cost) + Number(it.equipment_cost) + Number(it.subcontractor_cost)) * Number(it.quantity);
  return cost * (1 + Number(it.profit_pct) / 100);
};

export default function QuoteBuilder() {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const ctxId = searchParams.get("ctx");
  const crmReturnUrl = searchParams.get("return") || null;
  const [crmContext, setCrmContext] = useState(null); // {client_id, client_name, opportunity_id, opportunity_name, project_id, ...}
  const AUTOSAVE_KEY = "progere_draft_quote_v1";
  const [prompt, setPrompt] = useState(() => {
    try {
      const p = localStorage.getItem("progere_quick_prompt");
      if (p) { localStorage.removeItem("progere_quick_prompt"); return p; }
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && typeof d._prompt === "string") return d._prompt;
      }
    } catch {/* ignore */}
    return "";
  });
  const [generating, setGenerating] = useState(false);
  const [analyzingPhotos, setAnalyzingPhotos] = useState(false);
  const [genProgress, setGenProgress] = useState(0);       // 0..100
  const [genLabel, setGenLabel] = useState("");

  // Animate progress toward a target with ease-out (feels like a real AI job)
  const runGenProgress = (from, to, durationMs, label) => {
    setGenLabel(label);
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const raw = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - raw, 2.2);
      setGenProgress(from + (to - from) * eased);
      if (raw < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  const [photos, setPhotos] = useState([]); // [{file, url}]
  const [plans, setPlans] = useState([]); // [{file, name}]
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const planInputRef = useRef(null);
  const [storedPhotos, setStoredPhotos] = useState([]); // base64 thumbnails returned by AI to save with quote
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [autosaveTs, setAutosaveTs] = useState(null);
  const { clients: pClients, projects: pProjects, refresh: refreshGlobal } = usePrefetch();
  const [form, setForm] = useState(() => {
    // Restore in-progress draft if any
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.items?.length) {
          // Remove the prompt-shadow field before injecting into form state
          const { _prompt: _ignored, ...rest } = d;
          return rest;
        }
      }
    } catch {/* ignore */}
    return {
      client_id: "", client_name: "", project_id: "", project_name: "", project_address: "", work_type: "",
      work_description: "", scope_of_work: "", exclusions: "", conditions: "",
      payment_terms: "Net 30 jours", items: [blankItem()], language: lang,
    };
  });

  // Use prefetched data immediately, then refresh in background
  useEffect(() => {
    if (pClients.length) setClients(pClients); else axios.get(`${API}/clients`).then(({ data }) => setClients(data));
    if (pProjects.length) setProjects(pProjects); else axios.get(`${API}/projects`).then(({ data }) => setProjects(data));
  }, [pClients, pProjects]);

  // Load CRM handoff context (opportunity + client) when opened via ?ctx=xxx
  useEffect(() => {
    if (!ctxId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/integrations/crm/context/${ctxId}`);
        setCrmContext(data);
        // Pre-fill form with linked ids/names if available
        setForm((f) => ({
          ...f,
          client_id: data.client_id || f.client_id,
          client_name: data.client_name || f.client_name,
          project_id: data.project_id || f.project_id,
          project_name: data.project_name || f.project_name,
          opportunity_id: data.opportunity_id || f.opportunity_id,
          crm_context_id: ctxId,
        }));
      } catch (e) {
        console.warn("CRM context load failed", e?.response?.data);
      }
    })();
  }, [ctxId]);

  // Debounced autosave to localStorage (every 1.5s after last change to form OR prompt)
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ ...form, _prompt: prompt }));
        setAutosaveTs(new Date());
      } catch {/* quota */}
    }, 1500);
    return () => clearTimeout(id);
  }, [form, prompt]);

  const clearDraft = () => { try { localStorage.removeItem(AUTOSAVE_KEY); } catch {/* ignore */} setAutosaveTs(null); };

  const updateItem = (idx, key, val) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      items[idx].total = computeTotal(items[idx]);
      return { ...f, items };
    });
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const totals = form.items.reduce((s, it) => s + computeTotal(it), 0);
  const gst = totals * 0.05;
  const qst = totals * 0.09975;
  const grand = totals + gst + qst;

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setGenProgress(0);
    runGenProgress(0, 92, 18000, lang === "fr" ? "🧠 L'IA rédige votre soumission…" : "🧠 AI is drafting your quote…");
    try {
      const { data } = await axios.post(`${API}/ai/generate-quote`, { prompt, language: lang });
      const items = (data.items || []).map((it) => ({ ...blankItem(), ...it, total: computeTotal({ ...blankItem(), ...it }) }));
      setForm({
        ...form,
        project_name: data.project_name || form.project_name,
        work_type: data.work_type || form.work_type,
        scope_of_work: data.scope_of_work || "",
        exclusions: data.exclusions || "",
        conditions: data.conditions || "",
        items: items.length ? items : [blankItem()],
      });
      setGenProgress(100);
      setGenLabel(lang === "fr" ? "✓ Soumission prête" : "✓ Quote ready");
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur IA");
      setGenProgress(0);
    } finally { setGenerating(false); }
  };

  const onPhotosSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const valid = files.filter((f) => allowed.includes(f.type) && f.size <= 20 * 1024 * 1024);
    const remaining = 6 - photos.length;
    const toProcess = valid.slice(0, remaining);
    const newPhotos = [];
    for (const original of toProcess) {
      try {
        // Compress to ~1600px JPEG @ 85% (typical 8MB → ~300KB) to prevent Cloudflare timeouts
        const { dataUrl } = await compressImage(original, { maxDim: 1600, quality: 0.85 });
        // Rebuild a File from the compressed dataUrl so multipart upload still works
        const blob = await (await fetch(dataUrl)).blob();
        const compressed = new File([blob], original.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
        newPhotos.push({ file: compressed, url: URL.createObjectURL(compressed) });
      } catch (err) {
        console.warn("Compression failed, keeping original", err);
        newPhotos.push({ file: original, url: URL.createObjectURL(original) });
      }
    }
    setPhotos([...photos, ...newPhotos]);
    if (e.target) e.target.value = "";
  };

  const onPlansSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type === "application/pdf" && f.size <= 20 * 1024 * 1024);
    const newPlans = valid.slice(0, 3 - plans.length).map((file) => ({ file, name: file.name }));
    setPlans([...plans, ...newPlans]);
    if (e.target) e.target.value = "";
  };

  const removePhoto = (idx) => {
    URL.revokeObjectURL(photos[idx].url);
    setPhotos(photos.filter((_, i) => i !== idx));
  };

  const removePlan = (idx) => setPlans(plans.filter((_, i) => i !== idx));

  const generateFromPhotos = async () => {
    if (photos.length === 0 && plans.length === 0) return;
    setAnalyzingPhotos(true);
    setGenProgress(0);
    runGenProgress(0, 95, 45000, lang === "fr" ? "📸 Analyse des photos + rédaction IA…" : "📸 Analyzing photos + drafting…");
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      fd.append("language", lang);
      photos.forEach((p) => fd.append("photos", p.file));
      plans.forEach((p) => fd.append("plans", p.file));
      const { data } = await axios.post(`${API}/ai/generate-quote-from-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const items = (data.items || []).map((it) => ({ ...blankItem(), ...it, total: computeTotal({ ...blankItem(), ...it }) }));
      // Save photos returned by backend for persistence with quote
      if (Array.isArray(data._photo_b64s)) setStoredPhotos(data._photo_b64s);
      setForm({
        ...form,
        project_name: data.project_name || form.project_name,
        work_type: data.work_type || form.work_type,
        scope_of_work: data.scope_of_work || "",
        exclusions: data.exclusions || "",
        conditions: data.conditions || "",
        items: items.length ? items : [blankItem()],
      });
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur analyse photos");
    } finally { setAnalyzingPhotos(false); }
  };

  const save = async () => {
    if (!form.client_name || !form.project_name) {
      alert(lang === "fr" ? "Client et projet requis" : "Client and project required");
      return;
    }
    setSaving(true);
    try {
      const payload = { quote_number: "TEMP", ...form, photos: storedPhotos };
      const { data } = await axios.post(`${API}/quotes`, payload);
      clearDraft();
      refreshGlobal("quotes");
      nav(`/app/quotes/${data.id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        if (window.confirm(e.response.data.detail + "\n\n" + (lang === "fr" ? "Voir les options d'abonnement ?" : "View subscription options?"))) {
          nav("/app/billing");
        }
      } else {
        alert(e?.response?.data?.detail || "Erreur");
      }
    } finally { setSaving(false); }
  };

  const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(n || 0);

  return (
    <div>
      <PageHeader title={t.quotes.builder.title}
        action={autosaveTs && (
          <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(74,222,128,0.85)" }} data-testid="autosave-indicator">
            <CheckCircle2 size={13} />
            <span>
              {lang === "fr" ? "Brouillon sauvegardé" : "Draft saved"} ·{" "}
              {autosaveTs.toLocaleTimeString(lang === "fr" ? "fr-CA" : "en-CA", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        )}
      />

      {crmContext && (
        <div className="glass-strong p-5 mb-6 rounded-2xl" data-testid="crm-context-banner"
          style={{ border: "1px solid rgba(212,175,55,0.35)", background: "linear-gradient(135deg, rgba(212,175,55,0.06), rgba(192,0,0,0.05))" }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.35)" }}>
                <Link2 size={18} color="#D4AF37" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: "#D4AF37" }}>
                  {lang === "fr" ? "Ouvert depuis PROGÈRE CRM" : "Opened from PROGÈRE CRM"}
                </div>
                <div className="text-sm" style={{ color: "#F5F5F5" }}>
                  {crmContext.opportunity_id && (
                    <span className="inline-flex items-center gap-1.5 mr-3" data-testid="ctx-opportunity">
                      <Briefcase size={12} color="rgba(245,245,245,0.55)" />
                      <span style={{ color: "rgba(245,245,245,0.85)" }}>
                        {lang === "fr" ? "Opportunité" : "Opportunity"}:
                      </span>
                      <span style={{ color: "#F5F5F5", fontWeight: 600 }}>
                        {crmContext.opportunity_name || crmContext.opportunity_id.slice(0, 12)}
                      </span>
                    </span>
                  )}
                  {crmContext.client_id && (
                    <span className="inline-flex items-center gap-1.5" data-testid="ctx-client">
                      <span style={{ color: "rgba(245,245,245,0.55)" }}>·</span>
                      <span style={{ color: "rgba(245,245,245,0.85)" }}>
                        {lang === "fr" ? "Client" : "Client"}:
                      </span>
                      <span style={{ color: "#F5F5F5", fontWeight: 600 }}>
                        {crmContext.client_name || crmContext.client_id.slice(0, 12)}
                      </span>
                    </span>
                  )}
                </div>
                <div className="text-[11px] mt-1" style={{ color: "rgba(245,245,245,0.5)" }}>
                  {lang === "fr"
                    ? "Cette soumission sera automatiquement liée à l'opportunité CRM."
                    : "This quote will be automatically linked to the CRM opportunity."}
                </div>
              </div>
            </div>
            {crmReturnUrl && (
              <a href={crmReturnUrl} className="btn-ghost text-xs py-2 px-4 whitespace-nowrap"
                data-testid="ctx-return-btn">
                <ArrowLeft size={13} color="#D4AF37" />
                <span>{lang === "fr" ? "Retour à l'opportunité" : "Back to opportunity"}</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* AI Prompt */}
      <div className="glass-strong p-6 mb-6 glow-gold">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} color="#D4AF37" />
          <h3 className="font-heading text-lg font-semibold" style={{ color: "#F5F5F5" }}>{t.quotes.builder.ai_prompt}</h3>
        </div>
        <textarea className="input-dark mb-3" rows={3} placeholder={t.quotes.builder.ai_placeholder}
          value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-prompt" />
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={generate} disabled={generating} className="btn-gold" data-testid="ai-generate">
            {generating ? <><Spinner /> {t.quotes.builder.generating}</> : <><Sparkles size={14} /> {t.quotes.builder.generate}</>}
          </button>
          <MicButton
            lang={lang === "fr" ? "fr-CA" : "en-CA"}
            onTranscript={(txt) => setPrompt((p) => (p ? p + " " : "") + txt)}
            disabled={generating}
          />
          <span className="text-xs ml-1" style={{ color: "rgba(245,245,245,0.5)" }}>
            {lang === "fr" ? "Astuce : dictez la description du chantier au micro." : "Tip: dictate the project description with the mic."}
          </span>
        </div>
        <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-xl text-[11px]"
          style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)", color: "rgba(245,245,245,0.65)" }}
          data-testid="ai-disclaimer">
          <Sparkles size={11} color="#D4AF37" className="mt-0.5 flex-shrink-0" />
          <span>
            {lang === "fr"
              ? "Les résultats générés par l'intelligence artificielle doivent être vérifiés avant leur utilisation."
              : "AI-generated results must be reviewed before use."}
          </span>
        </div>

        {/* AI progress bar — pendant génération soumission/facture */}
        {(generating || analyzingPhotos) && (
          <div className="mt-3 rounded-xl p-3" data-testid="ai-progress-container"
            style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.3)" }}>
            <div className="flex items-center justify-between text-[11px] mb-1.5" style={{ color: "var(--pg-text-primary)" }}>
              <span className="font-semibold">{genLabel || (lang === "fr" ? "Génération en cours…" : "Generating…")}</span>
              <span className="font-mono font-bold" data-testid="ai-progress-percent" style={{ color: "#D4AF37" }}>
                {Math.round(genProgress)}%
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden relative" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div className="h-full rounded-full transition-all" data-testid="ai-progress-bar"
                style={{
                  width: `${Math.round(genProgress)}%`,
                  background: "linear-gradient(90deg, #F5D062 0%, #D4AF37 50%, #C00000 100%)",
                  boxShadow: "0 0 10px rgba(212,175,55,0.5)",
                }}>
                <div className="absolute inset-0 opacity-40"
                  style={{
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
                    animation: "shimmer 1.6s infinite",
                  }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Photo & Plan Vision AI */}
      <div className="glass-strong p-6 mb-6" style={{ border: "1px solid rgba(192, 0, 0, 0.25)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Camera size={18} color="#C00000" />
          <h3 className="font-heading text-lg font-semibold" style={{ color: "#F5F5F5" }}>{t.quotes.builder.upload_photos}</h3>
          <span className="ml-auto text-xs uppercase tracking-wider px-2 py-1 rounded-full"
            style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.3)" }}>
            {lang === "fr" ? "Nouveau · IA Vision" : "New · AI Vision"}
          </span>
        </div>
        <p className="text-xs mb-4" style={{ color: "rgba(245,245,245,0.55)" }}>{t.quotes.builder.photos_hint}</p>

        {/* Hidden inputs */}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple onChange={onPhotosSelected} className="hidden" data-testid="photos-input" />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={onPhotosSelected} className="hidden" data-testid="camera-input" />
        <input ref={planInputRef} type="file" accept="application/pdf" multiple onChange={onPlansSelected} className="hidden" data-testid="plans-input" />

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => fileInputRef.current?.click()} disabled={photos.length >= 6} className="btn-ghost text-xs py-2 px-4" data-testid="btn-add-photos">
            <ImageIcon size={14} color="#D4AF37" /> {t.quotes.builder.add_photos} ({photos.length}/6)
          </button>
          <button onClick={() => cameraInputRef.current?.click()} disabled={photos.length >= 6} className="btn-ghost text-xs py-2 px-4 md:inline-flex" data-testid="btn-take-photo">
            <Camera size={14} color="#D4AF37" /> {t.quotes.builder.take_photo}
          </button>
          <button onClick={() => planInputRef.current?.click()} disabled={plans.length >= 3} className="btn-ghost text-xs py-2 px-4" data-testid="btn-add-plans">
            <FileText size={14} color="#D4AF37" /> {t.quotes.builder.add_plans} ({plans.length}/3)
          </button>
        </div>

        {/* Photo previews grid */}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-2xl overflow-hidden group" style={{ border: "1px solid rgba(212,175,55,0.25)" }}>
                <img src={p.url} alt={`photo-${i}`} className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  style={{ background: "rgba(11,11,11,0.85)", border: "1px solid rgba(192,0,0,0.5)" }}
                  data-testid={`remove-photo-${i}`}>
                  <X size={14} color="#FF6B6B" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Plan previews list */}
        {plans.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {plans.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-2xl group" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)" }}>
                <FileText size={20} color="#D4AF37" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate" style={{ color: "#F5F5F5" }}>{p.name}</div>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>PDF · Plan</div>
                </div>
                <button onClick={() => removePlan(i)} className="p-1.5 rounded-full hover:bg-red-500/10" data-testid={`remove-plan-${i}`}>
                  <X size={14} color="#FF6B6B" />
                </button>
              </div>
            ))}
          </div>
        )}

        <button onClick={generateFromPhotos} disabled={analyzingPhotos || (photos.length === 0 && plans.length === 0)} className="btn-red" data-testid="ai-generate-photos">
          {analyzingPhotos ? <><Spinner /> {t.quotes.builder.analyzing_photos}</> : <><Camera size={14} /> {t.quotes.builder.generate_photos}</>}
        </button>

        {storedPhotos.length > 0 && (
          <p className="text-xs mt-3" style={{ color: "#4ADE80" }}>
            ✓ {storedPhotos.length} {lang === "fr" ? "image(s) seront enregistrée(s) avec la soumission" : "image(s) will be saved with the quote"}
          </p>
        )}
      </div>

      {/* Client/Project info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="glass-card p-6">
          <h3 className="font-heading text-base font-semibold mb-4" style={{ color: "#D4AF37" }}>{t.quotes.builder.client_info}</h3>
          <select className="input-dark mb-3" value={form.client_id} onChange={(e) => {
            const c = clients.find((x) => x.id === e.target.value);
            setForm({ ...form, client_id: e.target.value, client_name: c?.name || "", project_address: c?.address || form.project_address });
          }} data-testid="select-client">
            <option value="">{lang === "fr" ? "— Sélectionner un client —" : "— Select client —"}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input className="input-dark" placeholder={t.quotes.client + " *"} value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="client-name" />
        </div>
        <div className="glass-card p-6">
          <h3 className="font-heading text-base font-semibold mb-4" style={{ color: "#D4AF37" }}>{t.quotes.builder.project_info}</h3>
          <input className="input-dark mb-3" placeholder={t.quotes.project + " *"} value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="project-name" />
          <input className="input-dark mb-3" placeholder={t.projects_p.address} value={form.project_address} onChange={(e) => setForm({ ...form, project_address: e.target.value })} data-testid="project-address" />
          <input className="input-dark" placeholder={lang === "fr" ? "Type de travaux" : "Work type"} value={form.work_type} onChange={(e) => setForm({ ...form, work_type: e.target.value })} data-testid="work-type" />
        </div>
      </div>

      {/* Items */}
      <div className="glass-card p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4" style={{ color: "#F5F5F5" }}>{t.quotes.builder.items}</h3>
        <MissingItemsHint form={form} lang={lang} onAdd={(item) => {
          const newItem = { ...blankItem(), ...item };
          newItem.total = computeTotal(newItem);
          setForm((f) => ({ ...f, items: [...f.items, newItem] }));
        }} />
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>
                <th className="py-2">#</th><th>{t.quotes.builder.item_desc}</th><th>{t.quotes.builder.qty}</th><th>{t.quotes.builder.unit}</th>
                <th>{t.quotes.builder.mat}</th><th>{t.quotes.builder.lab}</th><th>{t.quotes.builder.eq}</th><th>{t.quotes.builder.sub}</th>
                <th>{t.quotes.builder.profit}</th><th>{t.quotes.builder.total_col}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i} className="border-t" style={{ borderColor: "rgba(245,245,245,0.05)" }}>
                  <td className="py-2 pr-2" style={{ color: "#D4AF37" }}>{i + 1}</td>
                  <td className="pr-2"><input className="input-dark" style={{ padding: "0.4rem 0.6rem" }} value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} /></td>
                  <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.quantity} onChange={(e) => updateItem(i, "quantity", parseFloat(e.target.value) || 0)} /></td>
                  <td className="pr-2"><input className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)} /></td>
                  <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.material_cost} onChange={(e) => updateItem(i, "material_cost", parseFloat(e.target.value) || 0)} /></td>
                  <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.labor_cost} onChange={(e) => updateItem(i, "labor_cost", parseFloat(e.target.value) || 0)} /></td>
                  <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.equipment_cost} onChange={(e) => updateItem(i, "equipment_cost", parseFloat(e.target.value) || 0)} /></td>
                  <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.subcontractor_cost} onChange={(e) => updateItem(i, "subcontractor_cost", parseFloat(e.target.value) || 0)} /></td>
                  <td className="pr-2"><input type="number" step="0.1" className="input-dark w-16" style={{ padding: "0.4rem 0.6rem" }} value={it.profit_pct} onChange={(e) => updateItem(i, "profit_pct", parseFloat(e.target.value) || 0)} /></td>
                  <td className="font-semibold whitespace-nowrap" style={{ color: "#D4AF37" }}>{fmt(computeTotal(it))}</td>
                  <td><button onClick={() => removeItem(i)} className="p-1.5 rounded-full hover:bg-red-500/10" data-testid={`remove-item-${i}`}><Trash2 size={14} color="#C00000" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={addItem} className="btn-ghost mt-4 text-sm" data-testid="add-item"><Plus size={14} /> {t.quotes.builder.add_item}</button>

        <div className="mt-6 ml-auto max-w-md text-sm space-y-2">
          <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}><span>{t.detail.subtotal}</span><span>{fmt(totals)}</span></div>
          <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}><span>{t.detail.gst}</span><span>{fmt(gst)}</span></div>
          <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}><span>{t.detail.qst}</span><span>{fmt(qst)}</span></div>
          <div className="flex justify-between pt-3 border-t font-bold text-lg" style={{ borderColor: "rgba(212,175,55,0.3)", color: "#D4AF37" }}><span>{t.detail.total}</span><span>{fmt(grand)}</span></div>
        </div>
      </div>

      {/* Scope/Excl/Cond */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {[
          { key: "scope_of_work", label: t.quotes.builder.scope },
          { key: "exclusions", label: t.quotes.builder.exclusions },
          { key: "conditions", label: t.quotes.builder.conditions },
        ].map((f) => (
          <div key={f.key} className="glass-card p-6">
            <h3 className="font-heading text-sm font-semibold mb-3" style={{ color: "#D4AF37" }}>{f.label}</h3>
            <textarea className="input-dark" rows={6} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
          </div>
        ))}
      </div>

      <div className="glass-card p-6 mb-6">
        <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.quotes.builder.payment}</label>
        <input className="input-dark mt-2" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="payment-terms" />
      </div>

      <div className="flex justify-end">
        <button className="btn-red" onClick={save} disabled={saving} data-testid="save-quote">
          {saving ? <><Spinner /> {t.quotes.builder.saving}</> : t.quotes.builder.save}
        </button>
      </div>
    </div>
  );
}
